import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Calendar, Clock, MapPin, Ticket, Users } from "lucide-react";
import CtaButton from "./CtaButton";
import RegisterButton from "./RegisterButton";

const POSTER_URL =
    "https://customer-assets-v7afamib.emergentagent.net/job_engulf-seminar-kl/artifacts/415de510a690418c_photo_2026-10-07_11-57-45.jpg";

const line = {
    hidden: { y: "115%" },
    show: (i) => ({
        y: "0%",
        transition: {
            duration: 1,
            delay: 0.2 + i * 0.14,
            ease: [0.22, 1, 0.36, 1],
        },
    }),
};

const DETAILS = [
    { icon: Calendar, label: "TARIKH", value: "31 October 2026", sub: "Saturday" },
    { icon: Clock, label: "MASA", value: "8:30 AM – 5:00 PM", sub: "Sesi penuh satu hari" },
    { icon: MapPin, label: "LOKASI", value: "Connexion Bangsar", sub: "Kuala Lumpur" },
    { icon: Ticket, label: "YURAN", value: "FREE ENTRY", sub: "Tiada bayaran" },
    { icon: Users, label: "KUOTA", value: "LIMITED 100 PAX", sub: "Tempat terhad" },
];

const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const yOrb = useTransform(scrollYProgress, [0, 1], [0, -70]);
    const yPoster = useTransform(scrollYProgress, [0, 1], [0, 90]);

    return (
        <section
            ref={ref}
            id="top"
            data-testid="hero-section"
            className="relative overflow-hidden pt-28 md:pt-36"
        >
            <motion.div
                style={{ y: yOrb }}
                className="pointer-events-none absolute -left-44 top-24 h-[480px] w-[480px] rounded-full bg-blood/20 blur-3xl"
            />
            <motion.div
                style={{ y: yPoster }}
                className="pointer-events-none absolute -right-44 top-4 h-[520px] w-[520px] rounded-full bg-volt/20 blur-3xl"
            />

            <div className="relative mx-auto grid max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-[1fr_0.94fr] lg:items-center lg:gap-10 lg:px-8">
                <div>
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.05 }}
                        className="flex flex-wrap items-center gap-3"
                    >
                        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/60">
                            QOD &amp; Cikgu Irfan present
                        </p>
                        <span className="rounded-full border border-blood/50 bg-blood/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-flame">
                            Featuring HFM
                        </span>
                    </motion.div>

                    <h1
                        data-testid="hero-headline"
                        className="mt-6 font-display text-[clamp(2.4rem,7vw,4.75rem)] font-extrabold leading-[0.95] tracking-tight"
                    >
                        {[
                            <span key="a" className="text-chrome-grad">GRAND SEMINAR</span>,
                            <span key="b" className="text-chrome-grad">ENGULF SUMMIT</span>,
                            <span key="c" className="text-duo-grad">2026</span>,
                        ].map((l, i) => (
                            <span key={i} className="block overflow-hidden pb-1">
                                <motion.span
                                    custom={i}
                                    variants={line}
                                    initial="hidden"
                                    animate="show"
                                    className="block"
                                >
                                    {l}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.75 }}
                        className="mt-7"
                    >
                        <p className="font-display text-lg font-bold text-white md:text-2xl">
                            TEKNIK SAMA, MARKET SAMA.{" "}
                            <span className="text-flame">
                                TAPI KENAPA ENTRY BOLEH BERBEZA?
                            </span>
                        </p>
                        <p className="mt-4 max-w-xl leading-relaxed text-white/65">
                            Belajar bagaimana nak memahami teknik Engulfing,
                            mengenal pasti confirmation dan membaca trend dengan
                            lebih tersusun bersama QOD &amp; Cikgu Irfan.
                        </p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.9 }}
                        className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center"
                    >
                        <CtaButton location="Hero" testId="hero-cta-button">
                            PM Admin Telegram
                        </CtaButton>
                        <RegisterButton testId="hero-register-button" />
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 1.1 }}
                        className="mt-8 inline-flex items-center gap-3 rounded-full border border-blood/40 bg-blood/10 px-4 py-2"
                        data-testid="hero-pax-badge"
                    >
                        <span className="relative flex h-2 w-2">
                            <span className="ping-soft absolute inline-flex h-full w-full rounded-full bg-blood" />
                            <span className="relative inline-flex h-2 w-2 rounded-full bg-blood" />
                        </span>
                        <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/80">
                            Limited 100 pax only
                        </span>
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.35 }}
                    style={{ y: yPoster }}
                    className="group relative lg:ml-auto lg:w-full lg:max-w-[520px]"
                    data-testid="hero-poster-frame"
                >
                    <div className="relative overflow-hidden rounded-[2rem] border border-white/15 bg-black/60 p-2 shadow-[0_40px_120px_-40px_rgba(227,27,35,0.35)] transition-transform duration-500 md:rotate-[1.5deg] md:group-hover:rotate-0">
                        <img
                            src={POSTER_URL}
                            alt="Poster rasmi Grand Seminar Engulf Summit 2026 oleh QOD & Cikgu Irfan, dianjurkan dengan HFM"
                            data-testid="hero-poster"
                            className="w-full rounded-3xl"
                        />
                    </div>
                </motion.div>
            </div>

            <div className="relative mt-16 border-t border-white/5 bg-white/[0.02] py-8 md:mt-20">
                <div className="mx-auto grid max-w-7xl grid-cols-2 gap-3 px-4 sm:grid-cols-3 sm:px-6 lg:grid-cols-5 lg:px-8">
                    {DETAILS.map((d, i) => (
                        <motion.div
                            key={d.label}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.3 }}
                            transition={{ duration: 0.6, delay: i * 0.08 }}
                            data-testid={`hero-detail-${i + 1}`}
                            className="glass rounded-2xl p-4"
                        >
                            <d.icon size={18} className="text-white/85" />
                            <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white/45">
                                {d.label}
                            </p>
                            <p className="mt-1 font-display text-sm font-bold text-white md:text-base">
                                {d.value}
                            </p>
                            <p className="mt-0.5 text-xs text-white/40">{d.sub}</p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Hero;
